import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';

export interface FavoriteProperty {
  id: string;
  title: string;
  price: number;
  image: string;
  address: string;
  type: 'sale' | 'rent';
  status: string;
  featured?: boolean;
  addedAt: string;
}

const FAVORITES_STORAGE_KEY = 'imobpanda_favorites';

export const useFavorites = () => {
  const [favorites, setFavorites] = useState<FavoriteProperty[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Carregar favoritos do localStorage na inicialização
  useEffect(() => {
    const loadFavorites = () => {
      try {
        const storedFavorites = localStorage.getItem(FAVORITES_STORAGE_KEY);
        if (storedFavorites) {
          const parsedFavorites = JSON.parse(storedFavorites);
          setFavorites(parsedFavorites);
        }
      } catch (error) {
        console.error('Erro ao carregar favoritos:', error);
      } finally {
        setLoading(false);
      }
    };

    loadFavorites();

    // Listener para mudanças no localStorage (para sincronização entre abas)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === FAVORITES_STORAGE_KEY && e.newValue) {
        try {
          const parsedFavorites = JSON.parse(e.newValue);
          setFavorites(parsedFavorites);
        } catch (error) {
          console.error('Erro ao sincronizar favoritos:', error);
        }
      }
    };

    // Listener para eventos customizados (sincronização entre componentes na mesma aba)
    const handleCustomFavoritesUpdate = (e: CustomEvent) => {
      if (e.detail && e.detail.favorites) {
        setFavorites(e.detail.favorites);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('favoritesUpdated', handleCustomFavoritesUpdate as EventListener);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('favoritesUpdated', handleCustomFavoritesUpdate as EventListener);
    };
  }, []);

  // Removido useEffect duplicado - salvamento agora é feito diretamente nas funções

  // Verificar se uma propriedade está nos favoritos
  const isFavorite = useCallback((propertyId: string) => {
    return favorites.some(fav => fav.id === propertyId);
  }, [favorites]);

  // Adicionar propriedade aos favoritos
  const addToFavorites = useCallback((property: Omit<FavoriteProperty, 'addedAt'>) => {
    if (isFavorite(property.id)) {
      toast({
        title: "Já está nos favoritos",
        description: "Esta propriedade já está na sua lista de favoritos.",
        variant: "default",
      });
      return;
    }

    const newFavorite: FavoriteProperty = {
      ...property,
      addedAt: new Date().toISOString(),
    };

    setFavorites(prev => {
      const newFavorites = [newFavorite, ...prev];
      // Salvar imediatamente no localStorage
      try {
        localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(newFavorites));
        // Disparar evento customizado para sincronização entre componentes
        window.dispatchEvent(new CustomEvent('favoritesUpdated', { 
          detail: { favorites: newFavorites, action: 'add', property: newFavorite }
        }));
      } catch (error) {
        console.error('Erro ao salvar favoritos:', error);
      }
      return newFavorites;
    });
    
    toast({
      title: "Adicionado aos favoritos",
      description: "Propriedade adicionada à sua lista de favoritos.",
    });
  }, [isFavorite, toast]);

  // Remover propriedade dos favoritos
  const removeFromFavorites = useCallback((propertyId: string) => {
    setFavorites(prev => {
      const newFavorites = prev.filter(fav => fav.id !== propertyId);
      // Salvar imediatamente no localStorage
      try {
        localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(newFavorites));
        // Disparar evento customizado para sincronização entre componentes
        window.dispatchEvent(new CustomEvent('favoritesUpdated', { 
          detail: { favorites: newFavorites, action: 'remove', propertyId }
        }));
      } catch (error) {
        console.error('Erro ao salvar favoritos:', error);
      }
      return newFavorites;
    });
    
    toast({
      title: "Removido dos favoritos",
      description: "Propriedade removida da sua lista de favoritos.",
    });
  }, [toast]);

  // Alternar favorito (adicionar ou remover)
  const toggleFavorite = useCallback((property: Omit<FavoriteProperty, 'addedAt'>) => {
    if (isFavorite(property.id)) {
      removeFromFavorites(property.id);
    } else {
      addToFavorites(property);
    }
  }, [isFavorite, addToFavorites, removeFromFavorites]);

  // Limpar todos os favoritos
  const clearFavorites = useCallback(() => {
    setFavorites([]);
    // Salvar imediatamente no localStorage
    try {
      localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify([]));
      // Disparar evento customizado para sincronização entre componentes
      window.dispatchEvent(new CustomEvent('favoritesUpdated', { 
        detail: { favorites: [], action: 'clear' }
      }));
    } catch (error) {
      console.error('Erro ao salvar favoritos:', error);
    }
    toast({
      title: "Favoritos limpos",
      description: "Todos os favoritos foram removidos.",
    });
  }, [toast]);

  // Obter contagem de favoritos
  const favoritesCount = favorites.length;

  return {
    favorites,
    favoritesCount,
    loading,
    isFavorite,
    addToFavorites,
    removeFromFavorites,
    toggleFavorite,
    clearFavorites,
  };
};
